/* oxlint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import type {
  FieldPath,
  FieldValues,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type * as Yup from 'yup';

import { useIsValid } from '../useIsValid';

export type LatitudeFormProps<
  LTFieldValues extends FieldValues = FieldValues,
  TContext = any,
> = UseFormProps<LTFieldValues, TContext> & {
  touchedFields?: Partial<LTFieldValues>;
  validationSchema?: Yup.ObjectSchema<LTFieldValues>;
};

export type UseLatitudeFormReturn<
  LTFieldValues extends FieldValues = FieldValues,
  TContext = any,
> = UseFormReturn<LTFieldValues, TContext> & {
  useIsValid: (paths: FieldPath<LTFieldValues>[]) => boolean;
};

export const useLatitudeForm = <
  LTFieldValues extends FieldValues = FieldValues,
  TContext = any,
>(
  props: LatitudeFormProps<LTFieldValues, TContext> = {},
): UseLatitudeFormReturn<LTFieldValues, TContext> => {
  const { validationSchema, touchedFields, ...formProps } = props;
  const [controlledTouchedFields, setControlledTouchedFields] = useState<
    FieldPath<LTFieldValues>[]
  >([]);

  const formValidationResolver = validationSchema
    ? {
        // Type assertion needed due to yupResolver type compatibility with react-hook-form
        // See: https://github.com/react-hook-form/resolvers/issues/271
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        resolver: yupResolver<LTFieldValues>(validationSchema) as any,
      }
    : {};

  const formHooks = useForm<LTFieldValues, TContext>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    ...formValidationResolver,
    ...formProps,
  });

  useEffect(() => {
    if (!touchedFields) {
      return;
    }

    // * This is a temporaty fix/solution to when we upgrade react-hook-form to v8
    // * We will be able to use the built-in setTouched function to the root of a
    // * field Array or nested object, right now, doing setValue(name, value, { shouldTouch: true }) doesn't
    // * mark the field as touched
    // *
    // * we could force the state if the array has items, in the items itself, but if its an empty array
    // * the RHF simply doesn't mark it as touched.
    // *
    const passedFormFieldNames = Object.keys(
      touchedFields,
    ) as FieldPath<LTFieldValues>[];
    setControlledTouchedFields(passedFormFieldNames);

    Object.entries(touchedFields).forEach(([formFieldName, formFieldValue]) => {
      // oxlint-disable-next-line @typescript-eslint/no-unsafe-argument
      formHooks.setValue(formFieldName as any, formFieldValue, {
        shouldDirty: false,
        shouldTouch: true,
        shouldValidate: true,
      });
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useFormIsValid = (fields: FieldPath<LTFieldValues>[]) => {
    return useIsValid(fields, {
      control: formHooks.control,
      controlledTouchedFields,
    });
  };

  return {
    ...formHooks,
    useIsValid: useFormIsValid,
  };
};
